import React, { useState, type ReactNode } from 'react';
import { Platform, Pressable, StyleSheet, Text } from 'react-native';

import { NumberInput } from './NumberInput';
import { useDimensionUnitController } from '../../request/dimensionUnits';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, fontWeight } from '../../theme/typography';
import {
  chipLabel,
  chipUnitName,
  labelWithStoredUnit,
  otherSide,
  placeholderForChip,
  type ChipSide,
  type FieldUnitPairId,
} from '../../utils/fieldUnitConversion';

import type { NumberInputProps } from './NumberInput';

type UnitNumberFieldProps = Omit<NumberInputProps, 'value' | 'onChangeText'> & {
  pair: FieldUnitPairId;
  value: string;
  onChangeText: (value: string) => void;
  /** Saves the chip side with the request. The typed number is not converted. */
  fieldKey?: string;
  /** When set, this chip is stored for that rack only. */
  unitScopeId?: number;
  /** View label that must follow the chip. The field owns the unit text. */
  renderLabel?: (labelText: string) => ReactNode;
};

export const UnitChip: React.FC<{
  label: string;
  accessibilityLabel: string;
  disabled?: boolean;
  onPress: () => void;
  testID?: string;
}> = ({ label, accessibilityLabel, disabled = false, onPress, testID }) => (
  <Pressable
    testID={testID}
    accessibilityRole="button"
    accessibilityLabel={accessibilityLabel}
    accessibilityState={{ disabled }}
    disabled={disabled}
    onPress={onPress}
    hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}
    style={[styles.chip, disabled && styles.chipDisabled]}
    {...(Platform.OS === 'web'
      ? {
          onMouseDown: (event: { preventDefault?: () => void }) => {
            event.preventDefault?.();
          },
        }
      : {})}
  >
    <Text style={[styles.chipText, disabled && styles.chipTextDisabled]}>
      {label}
    </Text>
  </Pressable>
);

export const UnitNumberField: React.FC<UnitNumberFieldProps> = ({
  pair,
  value,
  onChangeText,
  fieldKey,
  unitScopeId,
  disabled = false,
  readOnlyMode = false,
  editable = true,
  label = '',
  hideLabel = false,
  renderLabel,
  ...numberInputProps
}) => {
  const americaText = value == null ? '' : String(value);
  const stored = americaText.trim();
  const remembered = useDimensionUnitController(fieldKey, unitScopeId);
  const [localSide, setLocalSide] = useState<ChipSide>('america');
  const side = remembered?.side ?? localSide;
  const [draft, setDraft] = useState<string | null>(null);
  const display = draft ?? americaText;
  const unitName = chipUnitName(pair, side);
  const nextName = chipUnitName(pair, otherSide(side));
  const chipDisabled = disabled || editable === false;
  const showStoredUnit = readOnlyMode && stored !== '';
  const visibleLabel = labelWithStoredUnit(label, pair, showStoredUnit, side);
  const viewValue = display;

  const writeTyped = (text: string) => {
    if (text !== americaText) onChangeText(text);
  };

  const input = (
    <NumberInput
      showStepper={false}
      {...numberInputProps}
      placeholder={placeholderForChip(
        typeof numberInputProps.placeholder === 'string'
          ? numberInputProps.placeholder
          : undefined,
        pair,
        side,
      )}
      label={hideLabel || renderLabel ? label : visibleLabel}
      hideLabel={hideLabel || Boolean(renderLabel)}
      value={viewValue}
      disabled={disabled}
      readOnlyMode={readOnlyMode}
      editable={editable}
      onChangeText={text => {
        setDraft(text);
        writeTyped(text);
      }}
      onCommit={() => {
        if (draft !== null) writeTyped(draft);
        setDraft(null);
      }}
      endAccessory={
        readOnlyMode ? undefined : (
          <UnitChip
            testID={`unit-chip-${pair}`}
            label={chipLabel(pair, side)}
            accessibilityLabel={`${unitName}. Change unit to ${nextName}.`}
            disabled={chipDisabled}
            onPress={() => {
              const nextSide = otherSide(side);
              setDraft(null);
              setLocalSide(nextSide);
              remembered?.setSide(nextSide);
            }}
          />
        )
      }
    />
  );

  if (!renderLabel) return input;
  return (
    <>
      {renderLabel(visibleLabel)}
      {input}
    </>
  );
};

const styles = StyleSheet.create({
  chip: {
    marginRight: spacing.fieldVerticalGap,
    paddingHorizontal: spacing.formFieldStackedActionGap,
    paddingVertical: spacing.infoIconPadding,
    borderRadius: spacing.formControlBorderRadius,
    backgroundColor: colors.backgroundSecondary,
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
  },
  chipDisabled: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
  },
  chipText: {
    fontSize: fontSize.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  chipTextDisabled: {
    color: colors.textSecondary,
  },
});
